import { useState, useEffect, useCallback } from 'react';
import { useLongPress } from '@react-hooks/use-long-press';

import Buttons from './components/Buttons';
import Description from './components/Description';

const Show = ({ upload, ...params }) => {
  if (!upload) return null;
  return (
    <RealShow
      upload={upload}
      {...params}
    />
  );
};

const RealShow = ({
  upload,
  limit,
  container,
  setRect,
  rotatable,
  deletable,
  editable,
  selected,
  selection,
  onClick,
  onLongPress,
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const { url, previewUrl, orientation, metadata, contentType, filename } =
    upload;
  const image = contentType?.startsWith('image/');
  const video = contentType?.startsWith('video/');
  const isRotated = orientation === 'rotate-90' || orientation === 'rotate-270';
  const { height: imgHeight, width: imgWidth } = metadata || {};
  const imgRatio = imgWidth / imgHeight;

  const [parentSize, setParentSize] = useState({
    width: (container || window).innerWidth,
    height: (container || window).innerHeight,
  });

  useEffect(() => {
    if (!container?.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setParentSize({ width, height });
    });

    observer.observe(container.current);
    return () => observer.disconnect();
  }, [container]);

  useEffect(() => {
    setWidth(0);
    setHeight(0);
  }, [upload.id]);

  useEffect(() => {
    const { width: maxW, height: maxH } = parentSize;
    if (!maxW || !maxH) return;
    // quand on change d'upload, on définit width=0 et height=0
    // pour forcer le recalcul, ensuite c'est inutile
    if (height !== 0 || width !== 0) return;
    const screenRatio = maxW / maxH;

    if (isRotated) {
      if (imgWidth * screenRatio > imgHeight) {
        setWidth(maxH);
        setHeight(maxH / imgRatio);
      } else {
        setHeight(maxW);
        setWidth(maxW * imgRatio);
      }
    } else {
      if (imgWidth > imgHeight * screenRatio) {
        setWidth(maxW);
        setHeight(maxW / imgRatio);
      } else {
        setHeight(maxH);
        setWidth(maxH * imgRatio);
      }
    }
  }, [height, width, isRotated, imgRatio, imgWidth, imgHeight, parentSize]);

  useEffect(() => {
    if (!limit) return;
    if (width > height) {
      setWidth(limit);
      setHeight(limit / imgRatio);
    } else {
      setHeight(limit);
      setWidth(limit * imgRatio);
    }
  }, [width, height, limit, imgRatio]);

  useEffect(() => {
    if (!setRect) return;
    if (isRotated) {
      setRect({ width: height, height: width });
    } else {
      setRect({ width, height });
    }
  }, [setRect, width, height, isRotated]);

  const onClickUpload = useCallback(
    () => onClick && onClick(upload),
    [onClick, upload],
  );
  const onLongPressUpload = useCallback(
    () => onLongPress && onLongPress(upload),
    [onLongPress, upload],
  );
  const events = useLongPress({
    onClick: onClickUpload,
    onLongPress: onLongPressUpload,
  });

  const full = { width: '100%', height: '100%' };
  const selectedClass = selected
    ? 'border border-5 border-secondary bg-secondary/20'
    : '';

  // évite les glitchs lors des changements d'image
  if (width === 0 || height === 0) return null;

  return (
    <div
      style={{
        width: isRotated ? height : width,
        height: isRotated ? width : height,
      }}
      className={`relative`}
    >
      <div {...events}>
        {selected && (
          <div
            className={`absolute z-20 ${selectedClass}`}
            style={{ ...full, borderRadius: 8 }}
          />
        )}
        <Rotator {...{ width, height, orientation }}>
          {image && (
            <img
              style={{ ...full, borderRadius: 8 }}
              src={limit ? previewUrl : url}
            />
          )}
          {video && (
            <video
              style={{ ...full, borderRadius: 8 }}
              src={url}
              autoPlay
              muted
              loop
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          )}
        </Rotator>
      </div>
      {!image && !video && <div>{filename} : type de media inconnu</div>}
      <Buttons {...{ upload, selected, deletable, rotatable }} />
      <Description
        {...{ editable, upload, width, height, isRotated, onClick }}
      />
    </div>
  );
};

const Rotator = ({ width, height, orientation, children }) => {
  const size = { width, height };
  const isRotated = orientation === 'rotate-90' || orientation === 'rotate-270';
  const translate = isRotated
    ? `${(height - width) / 2}px ${(width - height) / 2}px`
    : 'none';
  const rotate = orientation.replace('rotate-', '') + 'deg';
  return (
    <div style={{ ...size, translate }}>
      <div style={{ ...size, rotate }}>{children}</div>
    </div>
  );
};

export default Show;
